import {
  Directive,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
  Renderer2,
  ViewContainerRef,
  ComponentRef,
  HostBinding,
  ContentChild,
  TemplateRef
} from '@angular/core';
import { Subject, Observable, of, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, takeUntil, filter } from 'rxjs/operators';
import { ComboboxDropdownComponent } from './combobox-dropdown.component';
import { ComboboxTranslations, ComboboxTranslationInput } from './combobox-translations.interface';
import { ComboboxTranslationService } from './combobox-translation.service';
import { ComboboxOptionDirective } from './combobox-option.directive';

export interface ComboboxFilter {
  caseSensitive?: boolean;
  operator?: 'startsWith' | 'contains' | ('startsWith' | 'contains')[];
}

export type ComboboxMode = 'filter' | 'select';

export type ComboboxDataSource<T = any> = 
  | string[] 
  | T[]
  | Set<string>
  | Set<T>
  | ((searchTerm: string, signal: AbortSignal) => Observable<string[] | T[]>)
  | ((searchTerm: string, signal: AbortSignal) => Promise<string[] | T[]>);

@Directive({
  selector: 'input[combobox]'
})
export class ComboboxDirective implements OnInit, OnDestroy {
  @Input() comboboxData: ComboboxDataSource = [];
  @Input() comboboxStrict: boolean = false;
  @Input() comboboxDebounce: number = 300;
  @Input() comboboxMinLength: number = 0;
  @Input() comboboxMaxResults: number = 100;
  @Input() comboboxPlaceholder: string = '';
  @Input() comboboxCaseSensitive: boolean = false;
  @Input() comboboxOperator: 'startsWith' | 'contains' | ('startsWith' | 'contains')[] = 'contains';
  @Input() comboboxDropdownHeight: number = 200;
  @Input() comboboxAutocomplete: boolean = false;
  @Input() comboboxMode: ComboboxMode = 'filter';
  @Input() comboboxFocusDelay: number = 300;
  @Input() comboboxTranslations?: ComboboxTranslationInput;
  @Input() comboboxValueProperty: string = 'value';
  @Input() comboboxDisplayProperty?: string;
  @Input() comboboxOptionTemplate?: TemplateRef<any>;
  
  @HostBinding('attr.role') role = 'combobox';
  @HostBinding('attr.aria-autocomplete') ariaAutocomplete = 'list';
  @HostBinding('attr.aria-expanded') ariaExpanded = 'false';
  @HostBinding('attr.aria-controls') ariaControls: string;
  @HostBinding('attr.aria-activedescendant') ariaActiveDescendant: string | null = null;
  @HostBinding('attr.autocomplete') autocomplete = 'off';

  private searchSubject$ = new Subject<string | null>();
  private destroy$ = new Subject<void>();
  private dropdownRef: ComponentRef<ComboboxDropdownComponent> | null = null;
  private currentValue: string = '';
  private selectedIndex: number = -1;
  private filteredOptions: any[] = [];
  private isOpen: boolean = false;
  private uniqueId: string;
  private abortController: AbortController | null = null;
  private isAutocompleting: boolean = false;
  private lastInputLength: number = 0;
  private pendingAutocomplete: boolean = false;
  private focusTimeout: any = null;
  private keyboardNavigationMode: boolean = false;
  private isProgrammaticUpdate: boolean = false;
  private resolvedTranslations: ComboboxTranslations;

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private translationService: ComboboxTranslationService
  ) {
    this.uniqueId = `combobox-${Math.random().toString(36).substr(2, 9)}`;
    this.ariaControls = `${this.uniqueId}-listbox`;
    this.resolvedTranslations = this.translationService.getResolvedTranslations();
  }

  ngOnInit(): void {
    // Resolve translations
    this.resolvedTranslations = this.translationService.getResolvedTranslations(this.comboboxTranslations);
    
    this.currentValue = this.elementRef.nativeElement.value || '';
    this.lastInputLength = this.currentValue.length;
    
    // Set up search with debouncing
    this.searchSubject$
      .pipe(
        debounceTime(this.comboboxDebounce),
        distinctUntilChanged(),
        // Filter out null values - used only to reset distinctUntilChanged state
        switchMap(searchTerm => {
          if (searchTerm === null) {
            return of(null);    
          }
          return this.loadOptions(searchTerm);
        }),
        // Remove null values before they reach the subscribe
        filter(options => options !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(options => {
        this.handleSearchResults(options);
        this.updateSelectedIndex();
        this.updateDropdown();
        this.scrollToHighlightedItem(); // Scroll to gray-highlighted item when typing
        this.tryApplyDeferredAutocomplete();
      });

    // Set initial ARIA attributes
    this.renderer.setAttribute(
      this.elementRef.nativeElement,
      'aria-haspopup',
      'listbox'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.abortPendingRequest();
    this.clearFocusTimeout();
    this.closeDropdown();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Cancel pending focus timeout to prevent race condition
    this.clearFocusTimeout();
    
    // Don't exit keyboard navigation mode if this is a programmatic update
    if (!this.isProgrammaticUpdate) {
      this.keyboardNavigationMode = false;
    }
    
    this.updateCurrentValue(event.target as HTMLInputElement);
    
    // Don't autocomplete or search during programmatic updates
    if (!this.isProgrammaticUpdate) {
      this.tryImmediateAutocomplete();
      this.handleInputSearch();
    }
  }

  @HostListener('focus')
  onFocus(): void {
    // Update current value from input in case it was set programmatically
    this.currentValue = this.elementRef.nativeElement.value || '';
    this.lastInputLength = this.currentValue.length;
    this.keyboardNavigationMode = false;
    
    const shouldOpen = this.comboboxMode === 'select' || 
                       this.currentValue.length >= this.comboboxMinLength ||
                       this.comboboxMinLength === 0;
    
    if (!shouldOpen) return;
    
    const searchTerm = this.comboboxMode === 'select' || !this.currentValue ? '' : this.currentValue;
    
    // Delay opening to prevent flashing when tabbing through fields
    this.clearFocusTimeout();
    this.focusTimeout = setTimeout(() => {
      // Load options immediately (bypass debounce)
      this.loadOptions(searchTerm).subscribe(options => {
        this.handleSearchResults(options);
        this.updateDropdown();
        this.tryApplyDeferredAutocomplete();
      });
      this.focusTimeout = null;
    }, this.comboboxFocusDelay);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent): void {
    // Accept autocomplete suggestion if still in autocomplete mode
    this.manageAutocomplete(true);
    
    // Cancel pending focus timeout
    this.clearFocusTimeout();
    
    // Small delay to ensure any click events are processed first
    setTimeout(() => {
      // Don't close if focus moved to dropdown
      if (!this.dropdownRef?.instance.elementRef.nativeElement.contains(document.activeElement)) {
        if (this.comboboxStrict && this.currentValue) {
          // Validate strict mode (respect case sensitivity)
          const isValid = this.filteredOptions.some(option => 
            this.normalizeValue(option) === this.normalizeValue(this.currentValue)
          );
          
          if (!isValid) {
            // Reset to empty or last valid value
            this.elementRef.nativeElement.value = '';
            this.currentValue = '';
          }
        }
        this.closeDropdown();
      }
    }, 10);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Handle accepting autocomplete suggestion
    if (this.isAutocompleting) {
      if (event.key === 'Tab' || event.key === 'ArrowRight' || event.key === 'Enter') {
        event.preventDefault();
        this.manageAutocomplete(true);
        this.closeDropdown();
        return;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.manageAutocomplete(false);
        this.closeDropdown();
        return;
      }
    }

    if (!this.isOpen) {
      // Open dropdown on ArrowDown if closed
      if (event.key === 'ArrowDown' && this.currentValue.length >= this.comboboxMinLength) {
        event.preventDefault();
        // Load options immediately (bypass debounce) to open on first press
        this.loadOptions(this.currentValue).subscribe(options => {
          this.handleSearchResults(options);
          this.updateDropdown();
        });
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateWithArrow('down');
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.navigateWithArrow('up');
        break;

      case 'Enter':
        // Only prevent default if we're accepting a value
        if (this.keyboardNavigationMode && this.isValidSelectedIndex()) {
          event.preventDefault();
          this.selectOption(this.filteredOptions[this.selectedIndex]);
        } else {
          // No selection to accept - close dropdown and allow default (e.g., form submission)
          this.closeDropdown();
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;

      case 'Tab':
        // Allow tab to close dropdown naturally
        this.closeDropdown();
        break;
    }
  }

  private loadOptions(searchTerm: string): Observable<string[]> {
    // Abort any pending request
    this.abortPendingRequest();
    
    if (typeof this.comboboxData === 'function') {
      // Create new AbortController for this request
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      
      // Async function provided (Observable or Promise)
      // In select mode, always pass empty string to get all results
      const queryTerm = this.comboboxMode === 'select' ? '' : searchTerm;
      
      const result = this.comboboxData(queryTerm, signal);
      
      // Convert Promise to Observable if needed
      const result$ = result instanceof Promise ? from(result) : result;
      
      return result$.pipe(
        catchError(error => {
          // Don't log abort errors
          if (error.name !== 'AbortError') {
            console.error('Error loading combobox options:', error);
          }
          return of([]);
        })
      );
    } else {
      // Array or Set provided
      const dataArray = Array.isArray(this.comboboxData)
        ? this.comboboxData
        : Array.from(this.comboboxData);

      if (this.comboboxMode === 'select') {
        // Select mode: return all options without filtering
        return of(dataArray.slice(0, this.comboboxMaxResults));
      } else {
        // Filter mode: filter locally
        const filtered = this.filterOptions(dataArray, searchTerm);
        return of(filtered.slice(0, this.comboboxMaxResults));
      }
    }
  }

  private filterOptions(options: any[], searchTerm: string): any[] {
    if (!searchTerm) {
      return options;
    }

    const searchValue = this.comboboxCaseSensitive ? searchTerm : searchTerm.toLowerCase();

    // If operator is an array, filter with precedence
    if (Array.isArray(this.comboboxOperator)) {
      const results: any[] = [];
      const processed = new Set<any>();

      // Process each operator in order (precedence)
      for (const op of this.comboboxOperator) {
        const matches = options.filter(option => {
          if (processed.has(option)) {
            return false; // Skip already processed items
          }

          const value = this.getValue(option);
          const optionValue = this.normalizeValue(value);
          const matches = this.matchesOperator(optionValue, searchValue, op);
          
          if (matches) {
            processed.add(option);
            return true;
          }
          return false;
        });

        results.push(...matches);
      }

      return results;
    }

    // Single operator
    return options.filter(option => {
      const value = this.getValue(option);
      const optionValue = this.normalizeValue(value);
      const operator = (this.comboboxOperator || 'contains') as 'startsWith' | 'contains';
      return this.matchesOperator(optionValue, searchValue, operator);
    });
  }

  private matchesOperator(optionValue: string, searchValue: string, operator: 'startsWith' | 'contains'): boolean {
    if (operator === 'startsWith') {
      return optionValue.startsWith(searchValue);
    } else {
      return optionValue.includes(searchValue);
    }
  }

  private abortPendingRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private openDropdown(): void {
    if (this.isOpen) {
      return;
    }

    const factory = this.viewContainerRef.createComponent(ComboboxDropdownComponent);
    this.dropdownRef = factory;
    
    this.updateSelectedIndex();

    const dropdown = this.dropdownRef.instance;
    dropdown.options = this.filteredOptions;
    dropdown.highlightedIndex = this.selectedIndex;
    dropdown.keyboardNavigationMode = this.keyboardNavigationMode;
    dropdown.listboxId = this.ariaControls;
    dropdown.optionPrefix = this.uniqueId;
    dropdown.maxHeight = this.comboboxDropdownHeight;
    dropdown.ariaLabel = this.resolvedTranslations.ariaLabel;
    dropdown.noResultsText = this.resolvedTranslations.noResultsText;
    dropdown.optionTemplate = this.comboboxOptionTemplate;
    dropdown.valueProperty = this.comboboxValueProperty;
    dropdown.displayProperty = this.comboboxDisplayProperty;

    // Position dropdown below input
    this.positionDropdown();

    // Subscribe to selection
    dropdown.optionSelected.subscribe((option: any) => {
      this.selectOption(option);
    });

    dropdown.highlightedIndexChange.subscribe((index: number) => {
      this.selectedIndex = index;
      this.updateAriaActiveDescendant();
    });

    // Add to DOM
    document.body.appendChild(this.dropdownRef.location.nativeElement);

    // Manually trigger change detection for initial render
    this.dropdownRef.changeDetectorRef.detectChanges();

    // Scroll to highlighted item if there is one
    if (this.selectedIndex >= 0) {
      setTimeout(() => {
        this.dropdownRef?.instance.scrollToHighlighted();
      }, 10);
    }

    this.isOpen = true;
    this.ariaExpanded = 'true';

    // Handle window resize
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowScroll, true);
  }

  private closeDropdown(): void {
    if (!this.isOpen || !this.dropdownRef) {
      return;
    }

    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowScroll, true);

    this.dropdownRef.destroy();
    this.dropdownRef = null;
    this.isOpen = false;
    this.ariaExpanded = 'false';
    this.ariaActiveDescendant = null;
    this.selectedIndex = -1;
  }

  private updateDropdown(): void {
    if (this.dropdownRef) {
      this.dropdownRef.instance.options = this.filteredOptions;
      this.dropdownRef.instance.highlightedIndex = this.selectedIndex;
      this.dropdownRef.instance.keyboardNavigationMode = this.keyboardNavigationMode;
      this.positionDropdown();
      // Manually trigger change detection for dynamically created component
      this.dropdownRef.changeDetectorRef.detectChanges();
    }
  }

  private positionDropdown(): void {
    if (!this.dropdownRef) {
      return;
    }

    const inputRect = this.elementRef.nativeElement.getBoundingClientRect();
    const dropdown = this.dropdownRef.location.nativeElement as HTMLElement;

    // Position below input
    dropdown.style.position = 'absolute';
    dropdown.style.top = `${inputRect.bottom + window.scrollY}px`;
    dropdown.style.left = `${inputRect.left + window.scrollX}px`;
    dropdown.style.width = `${inputRect.width}px`;
    dropdown.style.zIndex = '100000';
  }

  private selectNext(): void {
    if (this.filteredOptions.length === 0) {
      return;
    }

    this.selectedIndex = Math.min(
      this.selectedIndex + 1,
      this.filteredOptions.length - 1
    );

    this.updateDropdown();
    this.updateAriaActiveDescendant();
    this.scrollToHighlightedItem();
  }

  private selectPrevious(): void {
    if (this.filteredOptions.length === 0) {
      return;
    }

    this.selectedIndex = Math.max(this.selectedIndex - 1, 0);

    this.updateDropdown();
    this.updateAriaActiveDescendant();
    this.scrollToHighlightedItem();
  }

  private selectOption(option: any): void {
    const actualValue = this.getValue(option);
    
    this.currentValue = actualValue;
    this.setInputValueAndDispatchEvent(actualValue);
    this.lastInputLength = actualValue.length;
        
    // Reset searchSubject$ pipe state with null to clear distinctUntilChanged
    // This ensures future searches will work without triggering an unnecessary load
    this.searchSubject$.next(null);
    
    this.closeDropdown();
    
    // Return focus to input
    this.elementRef.nativeElement.focus();
  }

  private updateAriaActiveDescendant(): void {
    // Only set aria-activedescendant in keyboard navigation mode (WCAG compliance)
    if (this.keyboardNavigationMode && this.isValidSelectedIndex()) {
      this.ariaActiveDescendant = `${this.uniqueId}-option-${this.selectedIndex}`;
    } else {
      this.ariaActiveDescendant = null;
    }
  }

  private readonly onWindowResize = () => this.isOpen && this.positionDropdown();
  private readonly onWindowScroll = () => this.isOpen && this.positionDropdown();

  private applyAutocomplete(): boolean {
    const match = this.findAutocompleteMatch();
    if (match) {
      const actualValue = this.getValue(match);
      if (actualValue.length > this.currentValue.length) {
        // Preserve user's input case, only append the autocomplete part
        const autocompleteSuffix = actualValue.substring(this.currentValue.length);
        const fullValue = this.currentValue + autocompleteSuffix;
        this.setInputSelection(fullValue, this.currentValue.length, fullValue.length);
        this.isAutocompleting = true;
        return true;
      }
    }
    return false;
  }

  private manageAutocomplete(accept: boolean): void {
    if (!this.isAutocompleting) return;
    
    if (accept) {
      const input = this.elementRef.nativeElement;
      this.currentValue = input.value;
      this.lastInputLength = this.currentValue.length;
      this.isAutocompleting = false;
      this.setInputValueAndDispatchEvent(this.currentValue);
      input.setSelectionRange(this.currentValue.length, this.currentValue.length);
    } else {
      this.setInputSelection(this.currentValue, this.currentValue.length);
      this.isAutocompleting = false;
    }
  }

  private findMatchingIndex(options: any[], searchTerm: string): number {
    if (!searchTerm) return -1;
    
    const searchValue = this.normalizeValue(searchTerm);
    return options.findIndex(option => {
      const value = this.getValue(option);
      return this.normalizeValue(value).startsWith(searchValue);
    });
  }

  private handleSearchResults(options: any[]): void {
    this.filteredOptions = options;
    
    // Open dropdown when results are ready (if input is focused)
    if (!this.isOpen && this.elementRef.nativeElement === document.activeElement) {
      this.openDropdown();
    }
  }

  private updateSelectedIndex(): void {
    if (!this.currentValue || this.filteredOptions.length === 0) {
      this.selectedIndex = -1;
      return;
    }

    // Always use startsWith matching for highlighting (both modes)
    // This ensures the first matching item is highlighted while typing
    this.selectedIndex = this.findMatchingIndex(this.filteredOptions, this.currentValue);
  }

  private navigateWithArrow(direction: 'up' | 'down'): void {
    if (!this.keyboardNavigationMode) {
      // First arrow press: enter keyboard mode without changing selection
      this.keyboardNavigationMode = true;
      
      // If no item is highlighted, select the first item
      if (this.selectedIndex < 0 && this.filteredOptions.length > 0) {
        this.selectedIndex = 0;
      }
      
      // Clear any text selection and update input
      const input = this.elementRef.nativeElement;
      if (this.isValidSelectedIndex()) {
        const option = this.filteredOptions[this.selectedIndex];
        const actualValue = this.getValue(option);
        this.setInputValueAndDispatchEvent(actualValue);
        input.setSelectionRange(actualValue.length, actualValue.length);
        this.currentValue = actualValue;
        this.lastInputLength = actualValue.length;
      }
      this.updateDropdown();
      this.scrollToHighlightedItem();
    } else {
      // Already in keyboard mode: navigate
      direction === 'down' ? this.selectNext() : this.selectPrevious();
      this.updateInputFromSelection();
    }
  }

  private tryImmediateAutocomplete(): void {
    if (!this.comboboxAutocomplete || !this.currentValue) {
      this.lastInputLength = this.currentValue.length;
      this.pendingAutocomplete = false;
      return;
    }

    const input = this.elementRef.nativeElement;
    const hasSelection = input.selectionStart !== input.selectionEnd;
    const cursorAtEnd = input.selectionStart === this.currentValue.length;
    const lengthIncreased = this.currentValue.length > this.lastInputLength;
    
    this.lastInputLength = this.currentValue.length;
    
    if (cursorAtEnd && lengthIncreased && !hasSelection) {
      // Try to autocomplete with current list
      const success = this.applyAutocomplete();
      
      if (!success) {
        // Mark for retry after search completes
        this.pendingAutocomplete = true;
      } else {
        this.pendingAutocomplete = false;
      }
    } else {
      this.pendingAutocomplete = false;
    }
  }

  private tryApplyDeferredAutocomplete(): void {
    if (!this.pendingAutocomplete || !this.comboboxAutocomplete || !this.currentValue) {
      return;
    }

    const input = this.elementRef.nativeElement;
    const hasSelection = input.selectionStart !== input.selectionEnd;
    const cursorAtEnd = input.selectionStart === this.currentValue.length;
    
    // Only apply if user hasn't made changes since marking pending
    if (cursorAtEnd && !hasSelection) {
      this.applyAutocomplete();
    }
    
    this.pendingAutocomplete = false;
  }

  private updateCurrentValue(input: HTMLInputElement): void {
    const cursorPosition = input.selectionStart || 0;
    
    if (this.isAutocompleting) {
      this.currentValue = input.value.substring(0, cursorPosition);
      this.isAutocompleting = false;
    } else {
      this.currentValue = input.value;
    }
  }

  private handleInputSearch(): void {
    const shouldSearch = this.comboboxMode === 'select' || 
                        this.currentValue.length >= this.comboboxMinLength;

    if (shouldSearch) {
      this.searchSubject$.next(this.currentValue);
    } else {
      this.closeDropdown();
    }
  }

  private findAutocompleteMatch(): any | undefined {
    if (!this.currentValue || this.filteredOptions.length === 0) {
      return undefined;
    }

    const searchValue = this.normalizeValue(this.currentValue);
    return this.filteredOptions.find(option => {
      const value = this.getValue(option);
      return this.normalizeValue(value).startsWith(searchValue);
    });
  }

  private setInputSelection(value: string, start: number, end?: number): void {
    const input = this.elementRef.nativeElement;
    input.value = value;
    input.setSelectionRange(start, end ?? start);
  }

  private normalizeValue(value: string): string {
    return this.comboboxCaseSensitive ? value : value.toLowerCase();
  }

  /**
   * Get actual value from option (string or object)
   */
  private getValue(option: any): string {
    if (typeof option === 'string') {
      return option;
    }
    return option[this.comboboxValueProperty] || '';
  }

  /**
   * Check if option is a string or object
   */
  private isComplexOption(): boolean {
    return this.filteredOptions.length > 0 && typeof this.filteredOptions[0] !== 'string';
  }

  private isValidSelectedIndex(): boolean {
    return this.selectedIndex >= 0 && this.selectedIndex < this.filteredOptions.length;
  }

  private scrollToHighlightedItem(): void {
    if (!this.dropdownRef?.instance) {
      return;
    }
    setTimeout(() => {
      this.dropdownRef?.instance.scrollToHighlighted();
    }, 0);
  }

  private clearFocusTimeout(): void {
    if (this.focusTimeout) {
      clearTimeout(this.focusTimeout);
      this.focusTimeout = null;
    }
  }

  private setInputValueAndDispatchEvent(value: string): void {
    const input = this.elementRef.nativeElement;
    input.value = value;
    // Set flag to prevent keyboard navigation mode reset
    this.isProgrammaticUpdate = true;
    // Dispatch input event so Angular forms and other listeners are notified
    const event = new Event('input', { bubbles: true, cancelable: true });
    input.dispatchEvent(event);
    this.isProgrammaticUpdate = false;
  }

  private updateInputFromSelection(): void {
    if (this.isValidSelectedIndex()) {
      const selectedOption = this.filteredOptions[this.selectedIndex];
      const actualValue = this.getValue(selectedOption);
      this.currentValue = actualValue;
      this.setInputValueAndDispatchEvent(actualValue);
      this.lastInputLength = actualValue.length;
    }
  }
}
